import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { valueTypesTimeseries } from 'settings/valueTypesTimeseries'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'

type TValueTypeTimeseries = (typeof valueTypesTimeseries)[number]

export type TPointDeviceKind =
  'flowMeter' | 'irrigation' | 'pressureSensor' | 'soil' | 'weather' | 'windMachine'

export type TPointExternalVendorNames = SharedTypes.IrrigationScheduler.EIrrigationVendorName[]

const isValueTypeOfDeviceKind: Record<TPointDeviceKind, (valueType: TValueTypeTimeseries) => boolean> = {
  flowMeter: (valueType) => valueType.required_measurements.includes('irrigation-flow-meter'),
  irrigation: (valueType) => valueType.value_type_group_id === 'irrigation',
  pressureSensor: (valueType) =>
    valueType.required_measurements.some((measurement) => measurement.startsWith('water-pressure-')),
  soil: (valueType) => valueType.value_type_group_id === 'soil',
  weather: (valueType) => valueType.section_id === 'weather',
  windMachine: (valueType) => valueType.value_type_group_id === 'wind_machine',
}

const valueTypesById = new Map<string, TValueTypeTimeseries>(
  valueTypesTimeseries.map((valueType) => [valueType.id, valueType]),
)

export const getPointExternalVendorNames = ({
  point,
  ...filter
}: {
  point: Pick<TPoint, 'externalVendorNamesByValueType'> | null | undefined
} & ({ deviceKind: TPointDeviceKind } | { valueTypes: string[] })) => {
  const isIncluded =
    'deviceKind' in filter
      ? (valueTypeId: string) => {
          const valueType = valueTypesById.get(valueTypeId)

          return valueType ? isValueTypeOfDeviceKind[filter.deviceKind](valueType) : false
        }
      : (valueTypeId: string) => filter.valueTypes.includes(valueTypeId)

  const externalVendorNames = Object.entries(point?.externalVendorNamesByValueType ?? {})
    .filter(([valueTypeId]) => isIncluded(valueTypeId))
    .flatMap(([, vendorNames]) => vendorNames ?? [])

  return [...new Set(externalVendorNames)].sort()
}
